import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { X, Camera } from 'lucide-react';

const QRScanner = ({ onScan, onClose }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      setError('');
      setScanning(true);

      html5QrCodeRef.current = new Html5Qrcode('qr-reader');
      
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          onScan(decodedText);
          stopScanning();
        },
        (errorMessage) => {
          // Ignore error messages during scanning
        }
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError('No se pudo acceder a la cámara. Por favor, permite el acceso a la cámara.');
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    try {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      }
    } catch (err) {
      console.error('Error stopping scanner:', err);
    }
    setScanning(false);
  };

  return (
    <Card className="p-6" data-testid="qr-scanner">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Escanear Código QR</h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="qr-scanner-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="qr-scanner-error">
            {error}
          </div>
        )}

        <div 
          id="qr-reader" 
          className="w-full rounded-lg overflow-hidden bg-black"
          style={{ minHeight: scanning ? '300px' : '0' }}
          data-testid="qr-reader-container"
        />

        {!scanning && !error && (
          <div className="text-center py-8" data-testid="qr-scanner-instructions">
            <Camera className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-sm text-gray-600 mb-4">
              Haz clic en el botón para activar la cámara y escanear un código QR
            </p>
          </div>
        )}

        <div className="flex gap-2">
          {!scanning ? (
            <Button 
              onClick={startScanning} 
              className="w-full"
              data-testid="qr-scanner-start-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Activar Cámara
            </Button>
          ) : (
            <Button 
              onClick={stopScanning} 
              variant="destructive" 
              className="w-full"
              data-testid="qr-scanner-stop-btn"
            >
              Detener Escáner
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default QRScanner;
